import { useEffect, useRef, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { loadGoogleMapsForVenuePicker } from "../load-google-maps";
import {
  MIQAAT_VENUE_MAPS_DEFAULT_CENTER,
  MIQAAT_VENUE_MAPS_INCLUDED_REGION_CODES,
  MIQAAT_VENUE_MAPS_LOCATION_BIAS_RADIUS_M,
  buildVenueLocationFromPlace,
} from "../miqaat-venue-location";
import {
  mapPlaceSuggestionsToOptions,
  type VenuePlaceSearchOption,
} from "../venue-place-search-options";

const SUGGESTION_DEBOUNCE_MS = 300;

type Props = {
  apiKey: string;
  disabled?: boolean;
  onPlaceResolved: (fields: { latitude: number; longitude: number; name?: string }) => void;
};

/**
 * Places Autocomplete (New) via AutocompleteSuggestion, rendered as a normal
 * MUI TextField so height/chrome match react-admin inputs.
 */
export default function MiqaatVenuePlaceSearchField({
  apiKey,
  disabled = false,
  onPlaceResolved,
}: Props) {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<VenuePlaceSearchOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const placesRef = useRef<{
    AutocompleteSuggestion: typeof google.maps.places.AutocompleteSuggestion;
    AutocompleteSessionToken: typeof google.maps.places.AutocompleteSessionToken;
  } | null>(null);
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const predictionByIdRef = useRef<Map<string, google.maps.places.PlacePrediction>>(new Map());
  const requestIdRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const libs = await loadGoogleMapsForVenuePicker(apiKey);
        if (cancelled) {
          return;
        }
        placesRef.current = {
          AutocompleteSuggestion: libs.AutocompleteSuggestion,
          AutocompleteSessionToken: libs.AutocompleteSessionToken,
        };
        setReady(true);
        setError(null);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load Places.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiKey]);

  useEffect(() => {
    const places = placesRef.current;
    const query = inputValue.trim();
    if (!ready || !places || query.length < 2) {
      setOptions([]);
      setLoading(false);
      return;
    }

    const requestId = ++requestIdRef.current;
    setLoading(true);
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          if (!sessionTokenRef.current) {
            sessionTokenRef.current = new places.AutocompleteSessionToken();
          }
          const { suggestions } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input: query,
            includedRegionCodes: [...MIQAAT_VENUE_MAPS_INCLUDED_REGION_CODES],
            locationBias: {
              center: MIQAAT_VENUE_MAPS_DEFAULT_CENTER,
              radius: MIQAAT_VENUE_MAPS_LOCATION_BIAS_RADIUS_M,
            },
            sessionToken: sessionTokenRef.current,
          });
          if (requestId !== requestIdRef.current) {
            return;
          }
          const next = mapPlaceSuggestionsToOptions(suggestions);
          const predictionMap = new Map<string, google.maps.places.PlacePrediction>();
          for (const suggestion of suggestions) {
            const prediction = suggestion.placePrediction;
            if (prediction?.placeId) {
              predictionMap.set(prediction.placeId, prediction);
            }
          }
          predictionByIdRef.current = predictionMap;
          setOptions(next);
          setError(null);
        } catch (err) {
          if (requestId === requestIdRef.current) {
            setOptions([]);
            setError(err instanceof Error ? err.message : "Place search failed.");
          }
        } finally {
          if (requestId === requestIdRef.current) {
            setLoading(false);
          }
        }
      })();
    }, SUGGESTION_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [inputValue, ready]);

  const handleSelect = async (option: VenuePlaceSearchOption | null) => {
    if (!option) {
      return;
    }
    const prediction = predictionByIdRef.current.get(option.placeId);
    if (!prediction) {
      return;
    }
    try {
      setLoading(true);
      const place = prediction.toPlace();
      await place.fetchFields({ fields: ["displayName", "location"] });
      const location = place.location;
      if (!location) {
        setError("Selected place has no coordinates.");
        return;
      }
      onPlaceResolved(
        buildVenueLocationFromPlace({
          latitude: location.lat(),
          longitude: location.lng(),
          placeDisplayName: place.displayName,
        })
      );
      sessionTokenRef.current = null;
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load place details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Autocomplete
      fullWidth
      disabled={disabled || !ready}
      options={options}
      loading={loading}
      filterOptions={(x) => x}
      getOptionLabel={(option) => (typeof option === "string" ? option : option.label)}
      isOptionEqualToValue={(a, b) => a.placeId === b.placeId}
      inputValue={inputValue}
      onInputChange={(_, value, reason) => {
        if (reason === "reset") {
          return;
        }
        setInputValue(value);
      }}
      onChange={(_, value) => {
        void handleSelect(value);
      }}
      renderOption={(props, option) => {
        const { key, ...rest } = props as typeof props & { key?: string };
        return (
          <Box component="li" key={key ?? option.placeId} {...rest}>
            <Box>
              <Typography variant="body2">{option.mainText}</Typography>
              {option.secondaryText ? (
                <Typography variant="caption" color="text.secondary">
                  {option.secondaryText}
                </Typography>
              ) : null}
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Place search"
          placeholder="Search for a place in India…"
          margin="dense"
          fullWidth
          helperText={
            error ??
            "Select a place to fill the venue name and coordinates; refine with the pin below."
          }
          error={!!error}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
